import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Rating,
  Alert
} from '@mui/material';
import { Feedback as FeedbackIcon, Add as AddIcon } from '@mui/icons-material';

interface TripFeedback {
  id?: number;
  rating: number;
  comments: string;
  feedbackDate: string;
  customerName?: string;
}

interface TripFeedbackSectionProps {
  feedback: TripFeedback[] | null;
  onAdd: (newFeedback: TripFeedback) => Promise<void>;
  onRefresh?: () => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

const TripFeedbackSection: React.FC<TripFeedbackSectionProps> = ({
  feedback,
  onAdd,
  onRefresh,
  loading = false,
  disabled = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newFeedback, setNewFeedback] = useState<TripFeedback>({
    rating: 0,
    comments: '',
    feedbackDate: new Date().toISOString()
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const feedbackSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);

  useEffect(() => {
    if (shouldPreserveScroll && feedbackSectionRef.current) {
      setTimeout(() => {
        feedbackSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [feedback, isAdding, shouldPreserveScroll]);

  const handleAddFeedback = () => {
    setIsAdding(true);
    setNewFeedback({ rating: 0, comments: '', feedbackDate: new Date().toISOString() });
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewFeedback({ rating: 0, comments: '', feedbackDate: new Date().toISOString() });
    setErrorMessage('');
  };

  const handleSaveNew = async () => {
    setSaving(true);
    setErrorMessage('');
    setShouldPreserveScroll(true);
    if (!newFeedback.rating || !newFeedback.comments.trim()) {
      setErrorMessage('Please provide a rating and comments.');
      setSaving(false);
      return;
    }
    try {
      await onAdd(newFeedback);
      setSuccessMessage('Feedback added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsAdding(false);
      setNewFeedback({ rating: 0, comments: '', feedbackDate: new Date().toISOString() });
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to add feedback');
    } finally {
      setSaving(false);
    }
  };

  const SectionHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#464545',
        color: 'white',
        p: 2,
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FeedbackIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP FEEDBACK ({feedback?.length || 0})
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isAdding && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              color: 'white',
              borderColor: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              '&:disabled': {
                color: 'rgba(255,255,255,0.5)',
                borderColor: 'rgba(255,255,255,0.3)'
              }
            }}
            onClick={handleAddFeedback}
            disabled={loading || Boolean(feedback && feedback.length >= 1)}
          >
            ADD FEEDBACK
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Card ref={feedbackSectionRef} sx={{ mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
      <SectionHeader />
      <CardContent sx={{ backgroundColor: '#161616', color: 'white' }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
        {/* Add new feedback form */}
        {isAdding && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #666', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Add New Feedback
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={3} mb={2}>
              <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Rating
                </Typography>
                <Rating
                  value={newFeedback.rating}
                  onChange={(_, newValue) => {
                    setNewFeedback(prev => ({ ...prev, rating: newValue || 0 }));
                  }}
                  size="large"
                />
              </Box>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Comments
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={newFeedback.comments}
                onChange={(e) => {
                  setNewFeedback(prev => ({ ...prev, comments: e.target.value }));
                }}
                variant="outlined"
                size="small"
                placeholder="Enter feedback comments..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#999' },
                    '&.Mui-focused fieldset': { borderColor: '#059669' }
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ color: 'white', borderColor: 'white' }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveNew}
                sx={{ backgroundColor: '#059669' }}
                disabled={saving || !newFeedback.rating || !newFeedback.comments.trim()}
              >
                {saving ? <CircularProgress size={20} /> : 'Add Feedback'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Display existing feedback entries */}
        {feedback && feedback.length > 0 ? (
          <Box>
            {feedback.map((feedbackItem, index) => (
              <Box
                key={feedbackItem.id || index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #444',
                  borderRadius: 1,
                  '&:last-child': { mb: 0 }
                }}
              >
                <Box display="flex" flexWrap="wrap" gap={3} mb={2}>
                  <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Rating
                    </Typography>
                    <Rating value={feedbackItem.rating} readOnly size="large" />
                  </Box>

                  <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Customer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="white">
                      {feedbackItem.customerName || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="white">
                      {new Date(feedbackItem.feedbackDate).toLocaleDateString('en-IN')}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Comments
                  </Typography>
                  <Typography variant="body1" color="white">
                    {feedbackItem.comments || 'No comments provided'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : !isAdding ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No feedback available for this trip
            </Typography>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TripFeedbackSection;

